import React, { useState, useEffect } from 'react';

const PricingOverrideForm = ({
    basePriceInput,
    setBasePriceInput,
    baseSaving,
    onSaveBasePrice,

    overrideForm,
    handleOverrideInputChange,
    overrideSaving,
    onSaveOverride,

    currentOverride,
    clearingOverride,
    onClearOverride,

    pricingLoading,
    pricingError,
    pricingSuccess,

    planPriceDisplay,
    basePriceDisplay,
    effectivePrice
}) => {
    const formatCurrency = (value) => {
        if (value === undefined || value === null || value === '') return '-';
        const number = Number(value);
        return Number.isNaN(number) ? '-' : `$${number.toFixed(2)}`;
    };

    return (
        <div className="bg-[#282828] border border-[#3c3836] rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-[#ebdbb2]">Pricing & Overrides</h3>
                    <p className="text-sm text-[#928374]">Adjust customer-specific pricing without touching the base plan.</p>
                </div>
            </div>

            {pricingLoading && (
                <div className="text-sm text-[#928374]">Loading pricing details...</div>
            )}
            {pricingError && (
                <div className="text-sm text-red-600">{pricingError}</div>
            )}
            {pricingSuccess && (
                <div className="text-sm text-green-600">{pricingSuccess}</div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="p-3 bg-[#3c3836] rounded-lg">
                    <p className="text-xs font-medium text-[#928374] uppercase tracking-wide">Plan price (new customers)</p>
                    <p className="text-xl font-semibold text-[#ebdbb2] mt-1">{formatCurrency(planPriceDisplay)}</p>
                </div>
                <div className="p-3 bg-[#3c3836] rounded-lg">
                    <p className="text-xs font-medium text-[#928374] uppercase tracking-wide">Base price for this customer</p>
                    <p className="text-xl font-semibold text-[#ebdbb2] mt-1">{formatCurrency(basePriceDisplay)}</p>
                    <p className="text-xs text-[#928374] mt-1">Grandfathered unless overridden.</p>
                </div>
                <div className="p-3 bg-[#3c3836] rounded-lg">
                    <p className="text-xs font-medium text-[#928374] uppercase tracking-wide">Current invoice amount</p>
                    <p className="text-xl font-semibold text-[#ebdbb2] mt-1">
                        {effectivePrice ? formatCurrency(effectivePrice) : '—'}
                    </p>
                    {currentOverride && currentOverride.status === 'active' && (
                        <p className="text-xs text-blue-600 mt-1">Promo override in effect</p>
                    )}
                </div>
            </div>

            {/* Base price editor */}
            <div className="mt-6">
                <label className="text-sm font-medium text-[#bdae93]">Base monthly price</label>
                <div className="mt-2 flex flex-col md:flex-row gap-3">
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={basePriceInput}
                        onChange={(e) => setBasePriceInput(e.target.value)}
                        className="flex-1 px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                        placeholder="e.g. 89.99"
                    />
                    <button
                        onClick={onSaveBasePrice}
                        disabled={baseSaving || !basePriceInput}
                        className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {baseSaving ? 'Saving...' : 'Save Base Price'}
                    </button>
                </div>
                <p className="text-xs text-[#928374] mt-1">
                    Recurring billing uses this price when no override is active.
                </p>
            </div>

            {/* Override editor */}
            <div className="mt-8 border-t border-[#3c3836] pt-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="text-md font-semibold text-[#ebdbb2]">Promotional override</h4>
                        <p className="text-sm text-[#928374]">Apply temporary pricing for retention or promotions.</p>
                    </div>
                    {currentOverride && (
                        <button
                            onClick={onClearOverride}
                            disabled={clearingOverride}
                            className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {clearingOverride ? 'Clearing...' : 'Clear current override'}
                        </button>
                    )}
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm font-medium text-[#bdae93]">Override amount</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={overrideForm.amount}
                            onChange={(e) => handleOverrideInputChange('amount', e.target.value)}
                            className="mt-1 w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                            placeholder="e.g. 49.99"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-[#bdae93]">Start date (optional)</label>
                        <input
                            type="date"
                            value={overrideForm.startDate}
                            onChange={(e) => handleOverrideInputChange('startDate', e.target.value)}
                            className="mt-1 w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-[#bdae93]">End date (auto-revert)</label>
                        <input
                            type="date"
                            value={overrideForm.endDate}
                            onChange={(e) => handleOverrideInputChange('endDate', e.target.value)}
                            className="mt-1 w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-[#bdae93]">Revert to</label>
                        <select
                            value={overrideForm.revertType}
                            onChange={(e) => handleOverrideInputChange('revertType', e.target.value)}
                            className="mt-1 w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                        >
                            <option value="base">Customer base price</option>
                            <option value="plan">Current plan price</option>
                            <option value="custom">Custom amount</option>
                        </select>
                        {overrideForm.revertType === 'custom' && (
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={overrideForm.revertValue}
                                onChange={(e) => handleOverrideInputChange('revertValue', e.target.value)}
                                className="mt-2 w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                                placeholder="Custom revert amount"
                            />
                        )}
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-sm font-medium text-[#bdae93]">Internal notes</label>
                        <textarea
                            rows={3}
                            value={overrideForm.notes}
                            onChange={(e) => handleOverrideInputChange('notes', e.target.value)}
                            className="mt-1 w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                            placeholder="Reason, promo code, approval, etc."
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end mt-4">
                    <button
                        onClick={onSaveOverride}
                        disabled={overrideSaving || !overrideForm.amount}
                        className="px-5 py-2 bg-[#FFD700] text-[#0A2463] rounded-lg font-medium hover:bg-[#FACC15] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {overrideSaving ? 'Saving...' : 'Save Override'}
                    </button>
                </div>

                {currentOverride && (
                    <div className="mt-4 text-xs text-[#928374] space-y-1">
                        <p>Status: {currentOverride.status || 'scheduled'}</p>
                        {currentOverride.startDate && (
                            <p>Starts: {new Date(currentOverride.startDate).toLocaleDateString()}</p>
                        )}
                        {currentOverride.endDate && (
                            <p>Ends: {new Date(currentOverride.endDate).toLocaleDateString()}</p>
                        )}
                        {currentOverride.notes && (
                            <p>Notes: {currentOverride.notes}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PricingOverrideForm;
